/**
 * Utility functions for OpenSea-related operations, especially for shared contracts
 */
import type { NFTCollection } from "@/lib/api"
import { COLLECTIONS } from "@/lib/api"

/**
 * Checks if a token ID falls within a specified range
 * @param tokenId The token ID to check
 * @param rangeStart The start of the token ID range (inclusive)
 * @param rangeEnd The end of the token ID range (inclusive)
 * @returns Whether the token ID is within the range
 */
export function isTokenInRange(tokenId: string, rangeStart: string, rangeEnd: string): boolean {
  try {
    const tokenIdBigInt = BigInt(tokenId)
    const rangeStartBigInt = BigInt(rangeStart)
    const rangeEndBigInt = BigInt(rangeEnd)

    return tokenIdBigInt >= rangeStartBigInt && tokenIdBigInt <= rangeEndBigInt
  } catch (error) {
    console.error("Error comparing token ID:", error)
    return false
  }
}

/**
 * OpenSea shared contract address
 */
export const OPENSEA_SHARED_CONTRACT = "0x495f947276749ce646f68ac8c248420045cb7b5e"

/**
 * Get the OpenSea URL for a collection
 * @param collection The NFT collection
 * @returns The OpenSea URL for the collection
 */
export function resolveOpenSeaSlug(
  collection: Pick<NFTCollection, "id"> & { openSeaSlug?: string },
): string {
  try {
    if (collection.openSeaSlug) return collection.openSeaSlug

    // Canonical source of truth for slugs is `lib/api.ts` (collection.openSeaSlug).
    // This mapping is used strictly as a fallback when a collection does not specify openSeaSlug.
    const openSeaSlugs: Record<string, string> = {
      // Ethereum collections
      bayc: "boredapeyachtclub",
      mayc: "mutant-ape-yacht-club",
      coolcats: "cool-cats-nft",
      deadfellaz: "deadfellaz",
      azuki: "azuki",
      doodles: "doodles-official",
      pudgy: "pudgypenguins",
      wonderpals: "wonderpals",
      nyan: "nyan-cat-official",
      ilya: "ilyakazakov-originals",
      deekay: "letswalk",
      chimpers: "chimpersnft",
      vinnie: "letters-by-vinnie-hager",
      "forgotten-runes": "forgottenruneswizardscult",
      animo: "animo-official",
      chonks: "chonksociety",
      renga: "renga",
      cryptopunks: "cryptopunks",
      celmates: "cel-mates-by-mcbess",
      "garbage-friends": "garbage-friends-main",
      "a-kid-called-beast": "akidcalledbeast",
      "kaiju-kings": "kaiju-kingz",
      "1337-skulls": "1337-skulls",
      "kitaro-world": "kitaroworld",
      cryptomories: "cryptomories",
      cryptofoxes: "cryptofoxes-v2",
      opepen: "opepen-edition",
      beanz: "beanzofficial",
      "0n1force": "0n1-force",
      cyberkongz: "cyberkongz",
      chumchums: "chumchumsnft",
      degentoonz: "degentoonz-collection",
      goodvibes: "good-vibes-club",
      fanggang: "fanggangnft",
      frankysdinner: "frankys-dinner",
      spaceriders: "spaceriders",
      woodies: "woodies",
      "gutter-cat-gang": "guttercatgang",
      nakamigos: "nakamigos",
      alienfrens: "alienfrensnft",
      cryptoongoonz: "badbad",
      marscatsvoyage: "marscatsvoyage",
      meebits: "meebits",
      rareapepes: "rare-apepes",
      sappyseals: "sappy-seals",
      "peaceful-groupies": "peaceful-groupies",
      persona: "persona",
      figuregot: "figuregot",
      "doge-pound": "the-doge-pound",
      mocaverse: "mocaverse",
      "invisible-friends": "invisiblefriends",
      hpprs: "hpprs-collection",
      foxfam: "foxfam",
      "grillz-gang": "grillzgang",
      lilpudgys: "lilpudgys",
      "neko-darumas": "nekodarumas",
      alienboy: "thealienboy",
      "alien-boy": "thealienboy",
      nouns: "nouns",
      "memeland-potatoz": "memelandpotatoz",
              vogu: "vogu",
        transmissions909: "transmissions909",
        moonbirds: "moonbirds",
        "good-morning-cafe": "goodmorningcafe",
      clonex: "clonex",
      tigerbob: "tigerbobnft-genesis",
      cryptoadz: "cryptoadz-by-gremplin",
      mooncats: "acclimatedmooncats",
      toyfaces: "toyfaces",
      mekaverse: "mekaverse",
      robotos: "robotos-official",
      milady: "milady",
      creatures: "creatures",
      rubberduck: "rubber-duck-bath-party",
      chromie: "chromie-squiggle-by-snowfro",
      cacticrew: "thecacticrew",
      mfers: "mfers",
      // Use canonical slug to match lib/api.ts
      "based-minis": "based-minis",
      ftcg: "fake-trading-card-game",
      axie: "axie-ronin",
      samspratt: "sam-spratt-masks-of-luci",
      goblintown: "goblintownwtf",
      creepz: "genesis-creepz",
      boneys: "theboneys",
      nahfungible: "nah-fungible-bones",
      supducks: "supducks",
      beeple: "beeple-everydays",
      lt3: "lt3",
      regenerates: "re-gens",
      thehumanoids: "thehumanoids",
      murakami: "murakami-flowers-2022-official",
      moku: "moki-collection",
      boiz: "the-boi",
      killergf: "killergf",
      littlelemonfriends: "little-lemon-friends",
      ens: "ens",
    }

    return openSeaSlugs[collection.id] || collection.id
  } catch (error) {
    console.error(`Error resolving OpenSea slug for collection ${collection.id}:`, error)
    return collection.id
  }
}

/**
 * Get the display name for a collection from its OpenSea slug
 * This function maps OpenSea slugs back to the beautiful display names
 * used in the COLLECTIONS array to avoid duplicates in traits
 * 
 * @param slug The OpenSea collection slug (e.g., "chumchumsnft", "chonksociety")
 * @returns The beautiful display name (e.g., "Chum Chums NFT", "Chonks Society")
 */
export function getDisplayNameFromSlug(slug: string): string {
  try {
    // First, try to find by openSeaSlug property in COLLECTIONS
    const collectionBySlug = COLLECTIONS.find(col => col.openSeaSlug === slug)
    if (collectionBySlug) {
      return collectionBySlug.name
    }

    // Fallback: try to match by ID using the reverse mapping
    const slugToIdMap: Record<string, string> = {
      // Complete mapping for all 101 collections
      // OpenSea slugs to collection IDs
      "boredapeyachtclub": "bayc",
      "mutant-ape-yacht-club": "mayc", 
      "cool-cats-nft": "coolcats",
      "deadfellaz": "deadfellaz",
      "azuki": "azuki",
      "doodles-official": "doodles",
      "pudgypenguins": "pudgy",
      "wonderpals": "wonderpals",
      "nyan-cat-official": "nyan",
      "ilyakazakov-originals": "ilya",
      "letswalk": "deekay",
      "chimpersnft": "chimpers",
      "letters-by-vinnie-hager": "vinnie",
      "forgottenruneswizardscult": "forgotten-runes",
      "animo-official": "animo",
      "chonksociety": "chonks",
      "renga": "renga",
      "cryptopunks": "cryptopunks",
      "cel-mates-by-mcbess": "celmates",
      "garbage-friends-main": "garbage-friends",
      "akidcalledbeast": "a-kid-called-beast",
      "kaiju-kingz": "kaiju-kings",
      "1337-skulls": "1337-skulls",
      "kitaroworld": "kitaro-world",
      "cryptomories": "cryptomories",
      "cryptofoxes-v2": "cryptofoxes",
      "opepen-edition": "opepen",
      "beanzofficial": "beanz",
      "0n1-force": "0n1force",
      "cyberkongz": "cyberkongz",
      "chumchumsnft": "chumchums",
      "degentoonz-collection": "degentoonz",
      "good-vibes-club": "goodvibes",
      "fanggangnft": "fanggang",
      "frankys-dinner": "frankysdinner",
      "spaceriders": "spaceriders",
      "woodies": "woodies",
      "guttercatgang": "gutter-cat-gang",
      "nakamigos": "nakamigos",
      "alienfrensnft": "alienfrens",
      "badbad": "cryptoongoonz",
      "marscatsvoyage": "marscatsvoyage",
      "meebits": "meebits",
      "rare-apepes": "rareapepes",
      "sappy-seals": "sappyseals",
      "peaceful-groupies": "peaceful-groupies",
      "persona": "persona",
      "figuregot": "figuregot",
      "the-doge-pound": "doge-pound",
      "mocaverse": "mocaverse",
      "invisiblefriends": "invisible-friends",
      "hpprs-collection": "hpprs",
      "foxfam": "foxfam",
      "grillzgang": "grillz-gang",
      "lilpudgys": "lilpudgys",
      "nekodarumas": "neko-darumas",
      "thealienboy": "alienboy",
      "nouns": "nouns",
      "memelandpotatoz": "memeland-potatoz",
      "vogu": "vogu",
      "transmissions909": "transmissions909",
      "moonbirds": "moonbirds",
      "goodmorningcafe": "good-morning-cafe",
      "clonex": "clonex",
      "tigerbobnft-genesis": "tigerbob",
      "cryptoadz-by-gremplin": "cryptoadz",
      "acclimatedmooncats": "mooncats",
      "toyfaces": "toyfaces",
      "mekaverse": "mekaverse",
      "robotos-official": "robotos",
      "milady": "milady",
      "creatures": "creatures",
      "rubber-duck-bath-party": "rubberduck",
      "chromie-squiggle-by-snowfro": "chromie",
      "thecacticrew": "cacticrew",
      "mfers": "mfers",
      "based-minis": "based-minis",
      "fake-trading-card-game": "ftcg",
      "axie-ronin": "axie",
      "sam-spratt-masks-of-luci": "samspratt",
      "goblintownwtf": "goblintown",
      "genesis-creepz": "creepz",
      "theboneys": "boneys",
      "nah-fungible-bones": "nahfungible",
      "supducks": "supducks",
      "beeple-everydays": "beeple",
      "lt3": "lt3",
      "re-gens": "regenerates",
      "thehumanoids": "thehumanoids",
      "murakami-flowers-2022-official": "murakami",
      "moki-collection": "moku",
      "the-boi": "boiz",
      "killergf": "killergf",
      "little-lemon-friends": "littlelemonfriends",
      "ens": "ens",
      
      // Direct ID mappings for collections that appear as IDs in traits
      "bayc": "bayc",
      "mayc": "mayc",
      "coolcats": "coolcats",
      "deadfellaz": "deadfellaz",
      "azuki": "azuki",
      "doodles": "doodles",
      "pudgy": "pudgy",
      "wonderpals": "wonderpals",
      "nyan": "nyan",
      "ilya": "ilya",
      "deekay": "deekay",
      "chimpers": "chimpers",
      "vinnie": "vinnie",
      "forgotten-runes": "forgotten-runes",
      "animo": "animo",
      "chonks": "chonks",
      "renga": "renga",
      "cryptopunks": "cryptopunks",
      "celmates": "celmates",
      "garbage-friends": "garbage-friends",
      "a-kid-called-beast": "a-kid-called-beast",
      "kaiju-kings": "kaiju-kings",
      "1337-skulls": "1337-skulls",
      "kitaro-world": "kitaro-world",
      "cryptomories": "cryptomories",
      "cryptofoxes": "cryptofoxes",
      "opepen": "opepen",
      "beanz": "beanz",
      "0n1force": "0n1force",
      "cyberkongz": "cyberkongz",
      "chumchums": "chumchums",
      "degentoonz": "degentoonz",
      "goodvibes": "goodvibes",
      "fanggang": "fanggang",
      "frankysdinner": "frankysdinner",
      "spaceriders": "spaceriders",
      "woodies": "woodies",
      "gutter-cat-gang": "gutter-cat-gang",
      "axie": "axie",
      "nakamigos": "nakamigos",
      "alienfrens": "alienfrens",
      "cryptoongoonz": "cryptoongoonz",
      "marscatsvoyage": "marscatsvoyage",
      "meebits": "meebits",
      "rareapepes": "rareapepes",
      "sappyseals": "sappyseals",
      "peaceful-groupies": "peaceful-groupies",
      "ftcg": "ftcg",
      "persona": "persona",
      "figuregot": "figuregot",
      "doge-pound": "doge-pound",
      "mocaverse": "mocaverse",
      "based-minis": "based-minis",
      "invisible-friends": "invisible-friends",
      "hpprs": "hpprs",
      "foxfam": "foxfam",
      "grillz-gang": "grillz-gang",
      "neko-darumas": "neko-darumas",
      "alien-boy": "alien-boy",
      "nouns": "nouns",
      "memeland-potatoz": "memeland-potatoz",
      "vogu": "vogu",
      "good-morning-cafe": "good-morning-cafe",
      "clonex": "clonex",
      "transmissions909": "transmissions909",
      "moonbirds": "moonbirds",
      "tigerbob": "tigerbob",
      "cryptoadz": "cryptoadz",
      "mooncats": "mooncats",
      "toyfaces": "toyfaces",
      "mekaverse": "mekaverse",
      "lilpudgys": "lilpudgys",
      "samspratt": "samspratt",
      "robotos": "robotos",
      "milady": "milady",
      "creatures": "creatures",
      "rubberduck": "rubberduck",
      "chromie": "chromie",
      "cacticrew": "cacticrew",
      "mfers": "mfers",
      "goblintown": "goblintown",
      "creepz": "creepz",
      "boneys": "boneys",
      "nahfungible": "nahfungible",
      "supducks": "supducks",
      "beeple": "beeple",
      "lt3": "lt3",
      "regenerates": "regenerates",
      "thehumanoids": "thehumanoids",
      "murakami": "murakami",
      "moku": "moku",
      "killergf": "killergf",
      "littlelemonfriends": "littlelemonfriends",
      "boiz": "boiz",
      "thesadtimes": "thesadtimes",
      "killabears": "killabears",
      "basedfellas": "basedfellas",
      "khuga": "khuga",
      "starcatchers": "starcatchers",
      "pillheads": "pillheads",
      "ens": "ens",
    }

    const collectionId = slugToIdMap[slug]
    if (collectionId) {
      const collection = COLLECTIONS.find(col => col.id === collectionId)
      if (collection) {
        return collection.name
      }
    }

    // If no mapping found, return the slug as-is (fallback)
    console.warn(`[OPENSEA-UTILS] No display name mapping found for slug: ${slug}`)
    return slug
  } catch (error) {
    console.error(`[OPENSEA-UTILS] Error getting display name for slug ${slug}:`, error)
    return slug
  }
}

export function getOpenSeaUrl(
  collection:
    | NFTCollection
    | { id: string; name: string; image?: string; chain?: "ethereum" | "base" | "ronin"; openSeaSlug?: string },
): string {
  const slug = resolveOpenSeaSlug(collection)
  const chainParam = ("chain" in collection && collection.chain === "base") ? "?chain=base" : ""
  return `https://opensea.io/collection/${slug}${chainParam}`
}
